import { LitElement, html, css } from 'lit';
import { property, query, customElement } from 'lit/decorators.js';

import {
  authenticatedAPICall,
  isSuccessfulAPIResponse,
} from '../../client/api-client';
import { confirmPopup, showNotification } from '../../LanghoutBetonPortal';
import {
  formatDate,
  converStatusToReadable,
  converPickOrderStatusToInt,
  PickupOrderStatusReadyToBeProcessed,
  PickupOrderStatusBeingLoaded,
} from '../../util/util';
import { repeat } from 'lit/directives/repeat.js';
import {
  PickupOrder,
  Product,
  PickupSubOrder,
  PickupOrderList,
  getOrderIDs,
  getCustomerNames,
  getExternalIDs,
  getShippingIDs,
  getShippingCities,
} from '../../types/types';
import '../../elements/dropdown-multiselect-element/dropdown-multiselect-element';
import '../../elements/langhout-button/langhout-button';
import '../../elements/list-nagivation/list-navigation';
import '../../elements/search-dropdown-account/search-dropdown-account';
import '../langhout-beton-view-pickup-order/langhout-beton-view-pickup-order';
import '../../elements/langhout-checkbox/langhout-checkbox';
import { LanghoutCheckbox } from '../../elements/langhout-checkbox/langhout-checkbox';
import { DropdownMultiselectElement } from '../../elements/dropdown-multiselect-element/dropdown-multiselect-element';

import { messageIcon, refreshIcon, sortIcon } from '../../elements/icons/icons';
import { sharedStyles } from '../../elements/shared-styles';
import { SearchDropdownAccount } from '../../elements/search-dropdown-account/search-dropdown-account';

@customElement('langhout-beton-list-orders')
export class LanghoutBetonListOrders extends LitElement {
  static styles = [
    sharedStyles,
    css`
      .row {
        display: flex;
        flex-direction: row;
      }

      .row-flex {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        flex-flow: row wrap;
      }

      .filter-row {
        min-height: 130px;
      }

      .filter-column {
        margin-bottom: 20px;
      }

      .fat {
        font-weight: 900;
        color: var(--menu-color);
      }

      .label {
        font-weight: bold;
      }

      @media (max-width: 701px) {
        .label {
          text-align: left;
        }
      }

      .search-dropdown-account {
        width: 214px;
      }

      .product-column {
        text-align: left;
        width: 100%;
        min-width: 120px;
        min-height: calc(54px - 24px);
      }

      .order-id-click {
        font-weight: bold;
        text-decoration: underline;
        text-decoration-color: var(--primary-color);
      }

      .border-bot {
        border-bottom: 2px solid var(--menu-color);
      }

      .column-large {
        text-align: left;
        min-width: calc(350px - 20px);
        max-width: calc(350px - 20px);
      }

      .table-wrapper {
        overflow-x: auto;
      }

      .table-header {
        color: var(--text-color-light);
      }

      .header-column {
        background-color: var(--menu-color);
        padding-left: 12px;
      }

      .column-border-right {
        border-right: 2px solid var(--menu-color);
      }

      .navigation-wrapper {
        margin-top: 20px;
      }

      langhout-beton-view-pickup-order {
        position: absolute;
        top: 84px;
        right: -200vw;
      }

      :host([backAnimation]) langhout-beton-view-pickup-order {
        transition-duration: 500ms;
      }

      :host([viewOrder]) langhout-beton-view-pickup-order {
        transition-duration: 500ms;
        left: 0;
        right: 0;
        margin-left: auto;
        margin-right: auto;
        width: calc(100vw - 60px);
        height: calc(100vh -84px);
        top: 84px;
      }

      .checkbox-column {
        width: 23px;
        min-width: 23px;
      }

      .checkbox-wrapper {
        padding-left: 12px;
      }
      .checkbox-wrapper:hover {
        cursor: auto;
      }

      langhout-button {
        margin-left: 12px;
      }

      :host([disableButtons]) {
        pointer-events: none;
        opacity: 0.7;
      }

      .sort-icon-wrapper {
        display: flex;
        flex-direction: column;
        justify-content: center;
      }

      .sort-icon {
        transform: rotate(90deg);
      }

      .sort-icon:hover {
        cursor: pointer;
      }

      .sort-active {
        color: var(--text-color-main);
      }

      .order-id-column {
        padding-left: 10px;
        padding-right: 10px;
      }
      .message-icon-wrapper {
        padding-left: 10px;
      }

      #resetButton {
        margin-top: 20px;
      }
    `,
  ];

  private requestCounter = 0;

  private listTimer = 0;

  @property({ type: String })
  private lastPath = 'unknown';

  @property({ type: Array })
  private pickupOrders: PickupOrderList = [];

  @property({ type: Number })
  private pickupOrderCount = 0;

  @query('#fromDate')
  public fromDate?: HTMLInputElement;

  @query('#dropdownElement')
  public dropdownElement?: DropdownMultiselectElement;

  @query('#untilDate')
  public untilDate?: HTMLInputElement;

  @query('#orderID')
  public orderID?: HTMLInputElement;

  @query('#shippingID')
  public shippingID?: HTMLInputElement;

  @query('#externalID')
  public externalID?: HTMLInputElement;

  @query('#searchDropdownAccount')
  public searchDropdownAccount?: SearchDropdownAccount;

  @property({ type: Array, attribute: true })
  public selected: Array<string> = [
    'Ingevoerd',
    'Kan worden klaargezet',
    'Wordt klaargezet',
    'Klaargezet',
    'Start laden',
    'Start laden opgepakt',
    'Wordt geladen',
  ];

  private offset = 0;

  private limit = 50;

  @property({ type: Boolean, reflect: true })
  public viewOrder = false;

  @property({ type: Boolean, reflect: true })
  public backAnimation = false;

  @property({ type: Object })
  private currentViewOrder: PickupOrder = <PickupOrder>{
    sub_orders: <PickupSubOrder[]>[],
  };

  @property({ type: Boolean, reflect: true })
  private disableButtons = false;

  @property({ type: Array, reflect: true, attribute: true })
  private checkBoxes: boolean[] = [];

  private lastIDClicked = '';

  private orderByColumn = '';

  private orderByDirection = '';

  private shiftDown = false;

  private allCheckbox = false;

  constructor() {
    super();

    document.addEventListener('keydown', this.keyDownListener);
    document.addEventListener('keyup', this.keyUpListener);
    document.addEventListener(
      'refresh-pickup-orders',
      this.refreshPickupOrderListener,
    );

    document.addEventListener(
      'confirm_combine_pickup_orders',
      this.combineOrdersWithoutValidation as EventListener,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      'refresh-pickup-orders',
      this.refreshPickupOrderListener,
    );
    document.removeEventListener(
      'confirm_combine_pickup_orders',
      this.combineOrdersWithoutValidation,
    );
    document.removeEventListener('keydown', this.keyDownListener);
    document.removeEventListener('keydown', this.keyUpListener);
  }

  private keyDownListener = (e: KeyboardEvent) => {
    if (e.key === 'Shift') {
      this.shiftDown = true;
    }
  };

  private keyUpListener = (e: KeyboardEvent) => {
    if (e.key === 'Shift') {
      this.shiftDown = false;
    }
  };

  private refreshPickupOrderListener = () => {
    showNotification('Refreshing pickup orders');
    this.listWithBackoff();
  };

  protected firstUpdated(): void {
    if (this.dropdownElement) {
      this.dropdownElement.selectable = [
        'Ingevoerd',
        'Op afroep',
        'Kan worden klaargezet',
        'Wordt klaargezet',
        'Klaargezet',
        'Start laden',
        'Start laden opgepakt',
        'Wordt geladen',
        'Kan opgeruimd',
        'Wordt opgeruimd',
        'Afboeken',
      ];
      this.dropdownElement.selected = this.selected;
    }

    this.listPickupOrders();
  }

  render() {
    return html`
      <langhout-beton-view-pickup-order
        .currentViewOrder=${this.currentViewOrder}
        @close="${() => {
          this.viewOrder = false;
          this.listWithBackoffWithoutReset();
        }}"
        @reload=${() => {
          this.listWithBackoffWithoutReset();
        }}
      ></langhout-beton-view-pickup-order>
      ${this.renderFilterRow()}
      <div class="table-wrapper">
        <div class="row table-header">
          <div class="column header-column checkbox-column sort-icon-wrapper">
            <langhout-checkbox
              .checked=${this.allCheckbox}
              reverse
              @click="${() => {
                if (!this.shadowRoot) {
                  return;
                }
                this.allCheckbox = !this.allCheckbox;
                for (let i = 0; i < this.checkBoxes.length; i++) {
                  this.checkBoxes[i] = this.allCheckbox;
                }
                const temp = this.checkBoxes;
                this.checkBoxes = [];
                this.checkBoxes = temp;
              }}"
            ></langhout-checkbox>
          </div>
          <div class="column header-column product-column">
            <div class="row">
              <h3>Order ID</h3>
              <div class="fill"></div>
              <div
                class="sort-icon-wrapper ${this.isSortActive('order_id')}"
                @click="${() => {
                  this.orderColumns('order_id');
                }}"
              >
                ${sortIcon()}
              </div>
            </div>
          </div>
          <div class="column header-column product-column">
            <div class="row">
              <h3>Klant</h3>
              <div class="fill"></div>
              <div
                class="sort-icon-wrapper ${this.isSortActive('customer_name')}"
                @click="${() => {
                  this.orderColumns('customer_name');
                }}"
              >
                ${sortIcon()}
              </div>
            </div>
          </div>
          <div class="column header-column product-column">
            <div class="row">
              <h3>Plaatsnaam</h3>
              <div class="fill"></div>
              <div
                class="sort-icon-wrapper ${this.isSortActive('shipping_city')}"
                @click="${() => {
                  this.orderColumns('shipping_city');
                }}"
              >
                ${sortIcon()}
              </div>
            </div>
          </div>
          <div class="column header-column product-column">
            <div class="row">
              <h3>Vracht ID</h3>
              <div class="fill"></div>
              <div
                class="sort-icon-wrapper ${this.isSortActive('shipping_id')}"
                @click="${() => {
                  this.orderColumns('shipping_id');
                }}"
              >
                ${sortIcon()}
              </div>
            </div>
          </div>
          <div class="column header-column product-column">
            <div class="row">
              <h3>Klant ID</h3>
              <div class="fill"></div>
              <div
                class="sort-icon-wrapper ${this.isSortActive('external_id')}"
                @click="${() => {
                  this.orderColumns('external_id');
                }}"
              >
                ${sortIcon()}
              </div>
            </div>
          </div>
          <div class="column header-column product-column">
            <div class="row">
              <h3>Totaal gewicht</h3>
              <div class="fill"></div>
            </div>
          </div>
          <div class="column header-column product-column">
            <div class="row">
              <h3>Leverdatum</h3>
              <div class="fill"></div>
              <div
                class="sort-icon-wrapper ${this.isSortActive('pickup_date')}"
                @click="${() => {
                  this.orderColumns('pickup_date');
                }}"
              >
                ${sortIcon()}
              </div>
            </div>
          </div>
          <div class="column header-column product-column">
            <div class="row">
              <h3>Status</h3>
              <div class="fill"></div>
              <div
                class="sort-icon-wrapper ${this.isSortActive('status')}"
                @click="${() => {
                  this.orderColumns('status');
                }}"
              >
                ${sortIcon()}
              </div>
            </div>
          </div>
        </div>
        ${this.renderRows()}
      </div>
      <div class="navigation-wrapper">
        <list-navigation
          index="${this.offset}"
          limit="${this.limit}"
          total="${this.pickupOrderCount}"
          @change="${(e: CustomEvent) => {
            if (this.offset != e.detail.value) {
              this.offset = parseInt(e.detail.value);
              this.listWithBackoffWithoutReset();
            }
          }}"
        ></list-navigation>
      </div>
    `;
  }

  renderMessageIcon = (order: PickupOrder) => {
    if (order.comments.length === 0) {
      return html``;
    }
    return html` <div class="message-icon-wrapper">${messageIcon()}</div> `;
  };

  renderFilterRow = () => {
    return html`
      <div class="row-flex filter-row">
        <div class="column filter-column">
          <div class="label">Order ID</div>
          <div class="input-field">
            <input
              type="text"
              id="orderID"
              name="orderID"
              @input="${() => {
                this.listPickupOrders();
              }}"
            />
          </div>
        </div>
        <div class="column filter-column">
          <div class="label">Vracht ID</div>
          <div class="input-field">
            <input
              type="text"
              id="shippingID"
              name="shippingID"
              @input="${() => {
                this.listPickupOrders();
              }}"
            />
          </div>
        </div>
        <div class="column filter-column">
          <div class="label">Klant ID</div>
          <div class="input-field">
            <input
              type="text"
              id="externalID"
              name="externalID"
              @input="${() => {
                this.listPickupOrders();
              }}"
            />
          </div>
        </div>
        <div class="column filter-column search-dropdown-account">
          <div class="label">Klant</div>
          <div class="input-field">
            <search-dropdown-account
              id="searchDropdownAccount"
              @change="${() => {
                this.listPickupOrders();
              }}"
            ></search-dropdown-account>
          </div>
        </div>
        <div class="column filter-column">
          <div class="label">Status</div>
          <div class="input-field">
            <dropdown-multiselect-element
              id="dropdownElement"
              @change="${(e: CustomEvent) => {
                const newSelected = <Array<string>>JSON.parse(e.detail.value);
                this.selected = newSelected;
                this.listWithBackoff();
              }}"
            ></dropdown-multiselect-element>
          </div>
        </div>

        <div class="fill"></div>
        <div class="row filter-column">
          <langhout-button
            text="Selectie klaarzetten"
            @click="${async () => {
              this.disableButtons = true;
              await this.selectionReadyToBeProcessed();
              this.disableButtons = false;
            }}"
          ></langhout-button>
          <langhout-button
            text="Selectie combineren"
            @click="${async () => {
              this.disableButtons = true;
              await this.combineOrders();
              this.disableButtons = false;
            }}"
          ></langhout-button>
          <langhout-button
            text="Afroep invoeren"
            @click="${(): void => {
              window.location.href = '/create-pickup-order';
            }}"
          ></langhout-button>
        </div>
      </div>
      <div class="row-flex filter-row">
        <div class="column filter-column">
          <div class="label">Van</div>
          <div class="input-field">
            <input
              type="date"
              id="fromDate"
              name="fromDate"
              @change="${() => {
                this.listPickupOrders();
              }}"
            />
          </div>
        </div>
        <div class="column filter-column">
          <div class="label">Tot</div>
          <div class="input-field">
            <input
              type="date"
              id="untilDate"
              name="untilDate"
              @change="${() => {
                this.listPickupOrders();
              }}"
            />
          </div>
        </div>
        <langhout-button
          id="resetButton"
          text="Reset filters"
          @click="${(): void => {
            window.location.href = '/';
            location.reload();
          }}"
        ></langhout-button>
        <div class="fill"></div>
        <div
          @click="${() => {
            showNotification('Refreshing Pickup Orders');
            this.listWithBackoff();
          }}"
        >
          ${refreshIcon()}
        </div>
      </div>
    `;
  };

  renderRows = () => {
    return html` ${repeat(
      this.pickupOrders,
      pickupOrder => pickupOrder.id,
      (pickupOrder, index) => {
        let tableColumnClass = 'border-bot';
        if (index == this.pickupOrders.length - 1) {
          tableColumnClass = '';
        }
        return html`
          <div
            class="row table-row"
            @click="${() => {
              this.viewOrder = !this.viewOrder;
              this.backAnimation = true;
              this.setCurrentViewOrder(pickupOrder);
            }}"
          >
            <div
              class="checkbox-wrapper column-border-right ${tableColumnClass}"
              @click="${(e: Event) => {
                e.stopPropagation();
              }}"
            >
              <langhout-checkbox
                .checked="${this.checkBoxes[index]}"
                id="${pickupOrder.id}"
                class="checkbox"
                @click="${(e: Event) => {
                  e.stopPropagation();
                  this.updateCheckBoxes(pickupOrder);
                }}"
              ></langhout-checkbox>
            </div>
            <div
              class="row order-id-column table-column order-id-click column-border-right product-column ${tableColumnClass}"
            >
              <div>${getOrderIDs(pickupOrder)}</div>
              <div class="fill"></div>
              <div>${this.renderMessageIcon(pickupOrder)}</div>
            </div>
            <div
              class="column table-column column-border-right product-column ${tableColumnClass}"
            >
              ${getCustomerNames(pickupOrder)}
            </div>
            <div
              class="column table-column column-border-right product-column ${tableColumnClass}"
            >
              ${getShippingCities(pickupOrder)}
            </div>
            <div
              class="column table-column column-border-right product-column ${tableColumnClass}"
            >
              ${getShippingIDs(pickupOrder)}
            </div>
            <div
              class="column table-column column-border-right product-column ${tableColumnClass}"
            >
              ${getExternalIDs(pickupOrder)}
            </div>
            <div
              class="column table-column column-border-right product-column ${tableColumnClass}"
            >
              ${this.getWeightTotalText(pickupOrder)}
            </div>
            <div
              class="column table-column column-border-right product-column ${tableColumnClass}"
            >
              ${formatDate(pickupOrder.pickup_date)}
            </div>
            <div class="column table-column product-column ${tableColumnClass}">
              ${this.getStatusHTML(pickupOrder.status)}
            </div>
          </div>
        `;
      },
    )}`;
  };

  selectionReadyToBeProcessed = async () => {
    const idsToBeProcessed: string[] = this.getCheckboxIDs();
    if (idsToBeProcessed.length === 0) {
      showNotification(
        'Selecteer tenminste 1 pickup orders om klaar te zetten',
      );
      return;
    }
    await this.updatePickupOrderStatussesReadyToBeProcessed(idsToBeProcessed);
    this.resetCheckBoxes();
  };

  getStatusHTML = (status: number) => {
    if (status === PickupOrderStatusBeingLoaded) {
      return html` <div class="fat">${converStatusToReadable(status)}</div> `;
    }
    return html` ${converStatusToReadable(status)} `;
  };

  getWeightTotalText = (pickupOrder: PickupOrder): string => {
    let totalWeight = 0;

    pickupOrder.sub_orders.forEach((subOrder: PickupSubOrder) => {
      subOrder.products.forEach((p: Product) => {
        totalWeight += p.amount * p.weight;
      });
    });
    totalWeight = Math.round(totalWeight);

    return `${totalWeight.toLocaleString('nl-NL')} kg`;
  };

  updateCheckBoxes = (pickupOrder: PickupOrder) => {
    if (!this.shadowRoot) {
      return;
    }

    const clickedIndex = this.getIndexForID(pickupOrder.id);
    this.checkBoxes[clickedIndex] = !this.checkBoxes[clickedIndex];
    const setToValue = this.checkBoxes[clickedIndex];
    if (this.lastIDClicked != '' && this.shiftDown) {
      const lastClickedIndex = this.getIndexForID(this.lastIDClicked);
      for (
        let i = Math.min(clickedIndex, lastClickedIndex);
        i < Math.max(clickedIndex, lastClickedIndex);
        i++
      ) {
        this.checkBoxes[i] = setToValue;
      }
    }
    const temp = this.checkBoxes;
    this.checkBoxes = [];
    this.checkBoxes = temp;
    this.lastIDClicked = pickupOrder.id;
  };

  getIndexForID = (id: string) => {
    for (let i = 0; i < this.pickupOrders.length; i++) {
      if (this.pickupOrders[i].id === id) {
        return i;
      }
    }
    return 0;
  };

  isSortActive = (column: string): string => {
    if (this.orderByColumn === column) {
      return 'sort-active';
    }
    return '';
  };

  orderColumns = (column: string) => {
    if (this.orderByColumn === column && this.orderByDirection === '') {
      this.orderByDirection = 'asc';
    } else if (
      this.orderByColumn === column &&
      this.orderByDirection === 'asc'
    ) {
      this.orderByColumn = '';
      this.orderByDirection = '';
    } else {
      this.orderByColumn = column;
      this.orderByDirection = '';
    }
    this.listPickupOrders();
  };

  listWithBackoffWithoutReset = () => {
    clearInterval(this.listTimer);
    this.listTimer = window.setTimeout(() => {
      this.listPickupOrders();
    }, 50);
  };

  listWithBackoff = () => {
    clearInterval(this.listTimer);
    this.listTimer = window.setTimeout(() => {
      this.offset = 0;
      this.listPickupOrders();
    }, 50);
  };

  createFilterArray = (): string[] => {
    const filterArray: string[] = [];
    const statusFilterIntegerArray: Array<number> = [];
    this.selected.forEach(element => {
      statusFilterIntegerArray.push(...converPickOrderStatusToInt(element));
    });

    if (statusFilterIntegerArray.length == 0) {
      return [];
    }
    filterArray.push(`states=${statusFilterIntegerArray.join(',')}`);

    const fromDate = this.fromDate ? this.fromDate.value : '';
    if (fromDate != '') {
      filterArray.push(`from=${formatDate(fromDate)}`);
    }

    const accountCode = this.searchDropdownAccount
      ? this.searchDropdownAccount.selected?.code || ''
      : '';
    if (accountCode != '') {
      filterArray.push(`customer_code=${accountCode}`);
    }

    const untilDate = this.untilDate ? this.untilDate.value : '';
    if (untilDate != '') {
      filterArray.push(`until=${formatDate(untilDate)}`);
    }

    const orderID = this.orderID ? this.orderID.value : '';
    if (orderID != '') {
      filterArray.push(`order_id=${orderID}`);
    }

    const shippingID = this.shippingID ? this.shippingID.value : '';
    if (shippingID != '') {
      filterArray.push(`shipping_id=${shippingID}`);
    }

    const externalID = this.externalID ? this.externalID.value : '';
    if (externalID != '') {
      filterArray.push(`external_id=${externalID}`);
    }
    filterArray.push(`order_by=${this.orderByColumn}`);
    filterArray.push(`order_direction=${this.orderByDirection}`);
    return filterArray;
  };

  listPickupOrders = async () => {
    this.requestCounter++;
    const requestCounter = this.requestCounter;

    const filterArray = this.createFilterArray();
    let path = `/pickuporders?limit=${this.limit}&offset=${this.offset}`;
    const filter = filterArray.join('&');
    if (filter !== '') {
      path += '&' + filter;
    }
    const result = await authenticatedAPICall(path, 'GET', new Headers());
    if (!isSuccessfulAPIResponse(result)) {
      return;
    }
    if (requestCounter != this.requestCounter) {
      return;
    }
    const xCount = result.headers.get('x-count');
    if (xCount) {
      this.pickupOrderCount = parseInt(xCount);
    }

    this.pickupOrders = result.body;
    // If we have a selected view order overwrite.
    if (this.currentViewOrder.id != '') {
      this.pickupOrders.forEach((order: PickupOrder) => {
        if (this.currentViewOrder.id === order.id) {
          this.setCurrentViewOrder(order);
        }
      });
    }
    if (this.pickupOrders.length != this.checkBoxes.length) {
      this.checkBoxes = [];
      result.body.forEach(() => {
        this.checkBoxes.push(false);
      });
      this.allCheckbox = false;
      this.lastIDClicked = '';
    }
  };

  resetCheckBoxes = () => {
    this.checkBoxes = [];
  };

  combineOrders = async () => {
    if (!this.shadowRoot) {
      return;
    }
    const idsToCombine: string[] = this.getCheckboxIDs();
    if (idsToCombine.length < 2) {
      showNotification('Selecteer tenminste 2 pickup orders om te combineren');
      return;
    }

    const validationResult = await this.validateCombineOrdersCall(idsToCombine);
    if (validationResult && validationResult.length > 0) {
      confirmPopup({
        confirm_event: 'confirm_combine_pickup_orders',
        title: 'Pickup Order Combineren Bevestigen',
        description: validationResult.join('<br>'),
        with_checkbox: false,
        checkbox_text: '',
      });
      return;
    }

    this.combineOrdersCall(idsToCombine);
    this.resetCheckBoxes();
  };

  combineOrdersWithoutValidation = async () => {
    if (!this.shadowRoot) {
      return;
    }
    const idsToCombine: string[] = this.getCheckboxIDs();
    if (idsToCombine.length < 2) {
      showNotification('Selecteer tenminste 2 pickup orders om te combineren');
      return;
    }

    this.combineOrdersCall(idsToCombine);
    this.resetCheckBoxes();
  };

  validateCombineOrdersCall = async (orderIDs: string[]): Promise<string[]> => {
    const path = `/pickuporders/combine/validate`;
    const result = await authenticatedAPICall(
      path,
      'POST',
      new Headers(),
      JSON.stringify({
        ids: orderIDs,
      }),
    );
    if (isSuccessfulAPIResponse(result)) {
      return result.body.issues;
    }
    return [];
  };

  combineOrdersCall = async (orderIDs: string[]) => {
    const path = `/pickuporders/combine`;
    const result = await authenticatedAPICall(
      path,
      'POST',
      new Headers(),
      JSON.stringify({
        ids: orderIDs,
      }),
    );
    if (isSuccessfulAPIResponse(result) && result.body.success) {
      this.listWithBackoff();
    }
  };

  updatePickupOrderStatussesReadyToBeProcessed = async (orderIDs: string[]) => {
    const path = `/pickuporders/statusses`;
    const result = await authenticatedAPICall(
      path,
      'PUT',
      new Headers(),
      JSON.stringify({
        ids: orderIDs,
        status: PickupOrderStatusReadyToBeProcessed,
      }),
    );
    if (isSuccessfulAPIResponse(result) && result.body.success) {
      this.listWithBackoff();
    }
  };

  getCheckboxIDs = (): string[] => {
    if (!this.shadowRoot) {
      return [];
    }
    const checkboxes = this.shadowRoot.querySelectorAll('.checkbox');
    const selectedIDs: string[] = [];
    checkboxes.forEach(checkbox => {
      if ((<LanghoutCheckbox>checkbox).checked) {
        selectedIDs.push(checkbox.id);
      }
    });
    return selectedIDs;
  };

  setCurrentViewOrder = async (order: PickupOrder) => {
    this.currentViewOrder = order;
    const path = `/pickuporders/${order.id}`;
    const result = await authenticatedAPICall(path, 'GET', new Headers());
    if (isSuccessfulAPIResponse(result)) {
      this.currentViewOrder = result.body;
    }
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'langhout-beton-list-orders': LanghoutBetonListOrders;
  }
}
